/**
 * VisualizationPrimitives - Mini chart components for AI canvas
 *
 * Compact versions of larger visualizations that can be embedded in AI responses.
 * These are designed to be:
 * - Small and scannable
 * - Expandable on click
 * - Data-driven from tool results
 *
 * Types:
 * - MiniSankey: Compact spending flow diagram
 * - MiniTrajectory: Small trend/trajectory chart
 * - MiniHeatmap: Compact competitive heatmap
 * - MiniRadar: Small radar chart for scores
 * - SpendTrendChart: Shared fiscal year spend trend chart from @techstack/ui
 */

import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import './VisualizationPrimitives.css';

// Re-export SpendTrendChart from shared UI library
export { SpendTrendChart } from '@techstack/ui';

/**
 * MiniSankey - Compact spending flow visualization
 *
 * Shows a simplified horizontal flow chart of spending.
 * On click, can expand to full Sankey view.
 *
 * @param {Object} props
 * @param {Array} props.nodes - Nodes [{name, value, color}]
 * @param {Array} props.links - Links [{source, target, value}]
 * @param {string} props.title - Chart title
 * @param {function} props.onClick - Expand handler
 */
export function MiniSankey({ nodes = [], links = [], title, onClick }) {
  // Simplified view: show top 3 sources and targets.
  // Coerce values — backend numerics arrive as strings and string math
  // yields NaN widths (invisible bars).
  const topNodes = nodes.slice(0, 6).map(n => ({ ...n, value: Number(n.value) || 0 }));
  const totalValue = nodes.reduce((sum, n) => sum + (Number(n.value) || 0), 0);

  return (
    <div className="viz-mini-sankey" onClick={onClick}>
      {title && <div className="viz-mini-title">{title}</div>}
      <div className="viz-sankey-flow">
        {topNodes.map((node, i) => {
          const width = totalValue > 0 ? Math.max(20, (node.value / totalValue) * 100) : 20;
          return (
            <div
              key={i}
              className="viz-sankey-node"
              style={{
                width: `${width}%`,
                background: node.color || `hsl(${i * 60}, 70%, 60%)`,
              }}
              title={`${node.name}: $${(node.value / 1000000).toFixed(1)}M`}
            >
              <span className="viz-sankey-label">{node.name}</span>
            </div>
          );
        })}
      </div>
      {onClick && <div className="viz-mini-expand">Click to expand</div>}
    </div>
  );
}

/**
 * MiniTrajectory - Small trend/trajectory chart
 *
 * Shows a compact line/area chart for trends over time.
 *
 * @param {Object} props
 * @param {Array} props.data - [{date, value, ...}]
 * @param {string} props.dataKey - Key for the value
 * @param {string} props.title - Chart title
 * @param {string} props.color - Line color
 * @param {function} props.onClick - Expand handler
 */
export function MiniTrajectory({
  data = [],
  dataKey = 'value',
  title,
  color = 'var(--color-primary)',
  onClick,
}) {
  if (data.length === 0) {
    return (
      <div className="viz-mini-trajectory viz-empty">
        <span>No data</span>
      </div>
    );
  }

  // Coerce values (backend numerics arrive as strings → NaN in recharts)
  const chartData = data.map(d => ({ ...d, [dataKey]: Number(d?.[dataKey]) || 0 }));

  // Calculate trend
  const first = chartData[0]?.[dataKey] || 0;
  const last = chartData[chartData.length - 1]?.[dataKey] || 0;
  const trendPercent = first > 0 ? ((last - first) / first) * 100 : 0;
  const trendUp = trendPercent >= 0;

  return (
    <div className="viz-mini-trajectory" onClick={onClick}>
      {title && <div className="viz-mini-title">{title}</div>}
      <div className="viz-trajectory-chart">
        <ResponsiveContainer width="100%" height={60}>
          <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2}
              dot={false}
            />
            <Tooltip
              content={({ payload }) => {
                if (!payload?.[0]) return null;
                const val = payload[0].value;
                return (
                  <div className="viz-tooltip">
                    {typeof val === 'number' ? val.toLocaleString() : val}
                  </div>
                );
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="viz-trajectory-trend">
        <span className={`viz-trend ${trendUp ? 'up' : 'down'}`}>
          {trendUp ? '↑' : '↓'} {Math.abs(trendPercent).toFixed(1)}%
        </span>
      </div>
      {onClick && <div className="viz-mini-expand">Click to expand</div>}
    </div>
  );
}

/**
 * MiniHeatmap - Compact competitive heatmap
 *
 * Shows a small heatmap grid for competitive comparisons.
 *
 * @param {Object} props
 * @param {Array} props.data - [{name, values: [{category, score}]}]
 * @param {Array} props.categories - Category names
 * @param {string} props.title - Chart title
 * @param {function} props.onClick - Expand handler
 */
export function MiniHeatmap({ data = [], categories = [], title, onClick }) {
  const getColor = (score) => {
    if (score >= 80) return 'var(--color-success)';
    if (score >= 60) return 'var(--color-warning)';
    if (score >= 40) return '#f59e0b';
    return 'var(--color-error)';
  };

  return (
    <div className="viz-mini-heatmap" onClick={onClick}>
      {title && <div className="viz-mini-title">{title}</div>}
      <div className="viz-heatmap-grid">
        {/* Header row */}
        <div className="viz-heatmap-row header">
          <div className="viz-heatmap-cell label" />
          {categories.slice(0, 5).map((cat, i) => (
            <div key={i} className="viz-heatmap-cell header" title={cat}>
              {cat.slice(0, 3)}
            </div>
          ))}
        </div>
        {/* Data rows */}
        {data.slice(0, 4).map((row, i) => (
          <div key={i} className="viz-heatmap-row">
            <div className="viz-heatmap-cell label" title={row.name}>
              {row.name.slice(0, 8)}
            </div>
            {row.values?.slice(0, 5).map((val, j) => (
              <div
                key={j}
                className="viz-heatmap-cell"
                style={{ background: getColor(val.score) }}
                title={`${val.category}: ${val.score}`}
              />
            ))}
          </div>
        ))}
      </div>
      {onClick && <div className="viz-mini-expand">Click to expand</div>}
    </div>
  );
}

/**
 * MiniRadar - Small radar chart for multi-dimensional scores
 *
 * Shows a compact radar/spider chart for comparing scores across dimensions.
 *
 * @param {Object} props
 * @param {Array} props.data - [{axis, value}]
 * @param {string} props.title - Chart title
 * @param {string} props.color - Fill color
 * @param {function} props.onClick - Expand handler
 */
export function MiniRadar({
  data = [],
  title,
  color = 'var(--color-primary)',
  onClick,
}) {
  if (data.length === 0) {
    return (
      <div className="viz-mini-radar viz-empty">
        <span>No data</span>
      </div>
    );
  }

  // Coerce values (backend numerics arrive as strings → NaN in recharts)
  const chartData = data.map(d => ({ ...d, value: Number(d?.value) || 0 }));
  const avgScore = chartData.reduce((sum, d) => sum + d.value, 0) / chartData.length;

  return (
    <div className="viz-mini-radar" onClick={onClick}>
      {title && <div className="viz-mini-title">{title}</div>}
      <div className="viz-radar-chart">
        <ResponsiveContainer width="100%" height={100}>
          <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="80%">
            <PolarGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <PolarAngleAxis
              dataKey="axis"
              tick={{ fontSize: 8, fill: 'var(--color-text-muted)' }}
            />
            <Radar
              dataKey="value"
              stroke={color}
              fill={color}
              fillOpacity={0.3}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <div className="viz-radar-score">
        Avg: <strong>{avgScore.toFixed(0)}</strong>
      </div>
      {onClick && <div className="viz-mini-expand">Click to expand</div>}
    </div>
  );
}

/**
 * MiniBarChart - Simple bar chart for comparisons
 *
 * @param {Object} props
 * @param {Array} props.data - [{name, value}]
 * @param {string} props.title - Chart title
 * @param {string} props.color - Bar color
 * @param {function} props.onClick - Expand handler
 */
export function MiniBarChart({
  data = [],
  title,
  color = 'var(--color-primary)',
  onClick,
}) {
  // Coerce values (backend numerics arrive as strings → NaN in recharts)
  const chartData = data.slice(0, 5).map(d => ({ ...d, value: Number(d?.value) || 0 }));

  return (
    <div className="viz-mini-bar" onClick={onClick}>
      {title && <div className="viz-mini-title">{title}</div>}
      <div className="viz-bar-chart">
        <ResponsiveContainer width="100%" height={60}>
          <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <Bar dataKey="value" fill={color} radius={[2, 2, 0, 0]} />
            <Tooltip
              content={({ payload }) => {
                if (!payload?.[0]) return null;
                return (
                  <div className="viz-tooltip">
                    {payload[0].payload.name}: {Number(payload[0].value).toLocaleString()}
                  </div>
                );
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {onClick && <div className="viz-mini-expand">Click to expand</div>}
    </div>
  );
}

/**
 * SparkLine - Ultra-compact inline chart
 *
 * @param {Object} props
 * @param {Array} props.data - Array of numbers
 * @param {string} props.color - Line color
 * @param {number} props.height - Chart height
 */
export function SparkLine({ data = [], color = 'var(--color-primary)', height = 20 }) {
  if (data.length < 2) return null;

  const chartData = data.map((value, i) => ({ value: Number(value) || 0, index: i }));

  return (
    <div className="viz-sparkline" style={{ height }}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
